import { useOutletContext } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Printer, Copy, Check, QrCode } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function QRKitPage() {
  const { biz } = useOutletContext();
  const [copied, setCopied] = useState(false);

  if (!biz) return null;
  const hubUrl = `${window.location.origin}/h/${biz.slug}`;
  const accent = biz.accent_color || "#C5A880";

  const download = () => {
    const canvas = document.getElementById("hub-qr-canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${biz.slug}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR code downloaded");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(hubUrl).then(
      () => {
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 1500);
      },
      () => toast.error("Couldn't copy — please copy the link manually")
    );
  };

  const printCard = () => {
    const canvas = document.getElementById("hub-qr-canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=900,height=1200");
    w.document.write(`
      <html><head><title>${biz.name} — Guest Card</title>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=Manrope:wght@400;600&display=swap" rel="stylesheet">
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:'Manrope',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#f5f2ec;}
        .card{width:520px;background:#0A0D10;color:#FDFBF7;border-radius:14px;padding:56px 48px;text-align:center;}
        .label{letter-spacing:.3em;text-transform:uppercase;font-size:11px;color:${accent};margin-bottom:18px;}
        h1{font-family:'Playfair Display',serif;font-size:36px;line-height:1.1;margin-bottom:10px;}
        .tag{color:#A1A7B0;font-size:15px;margin-bottom:34px;}
        .qr{background:#fff;padding:22px;border-radius:12px;display:inline-block;}
        .scan{margin-top:30px;font-family:'Playfair Display',serif;font-size:22px;}
        .sub{color:#A1A7B0;font-size:13px;margin-top:8px;}
        .url{color:${accent};font-size:13px;margin-top:6px;word-break:break-all;}
      </style></head>
      <body><div class="card">
        <div class="label">${biz.business_type || "Digital Concierge"}</div>
        <h1>${biz.name}</h1>
        <div class="tag">${biz.tagline || "Your digital concierge"}</div>
        <div class="qr"><img src="${dataUrl}" width="240" height="240"/></div>
        <div class="scan">Scan for your digital concierge</div>
        <div class="sub">Room info · local guides · services · live updates</div>
        <div class="url">${hubUrl}</div>
      </div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-8" data-testid="qr-kit-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">QR Code Kit</h1>
        <p className="text-muted-foreground text-sm">Share your hub anywhere. Download the QR or print a table card for rooms and reception.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-8 flex flex-col items-center text-center">
          <div className="p-4 bg-white rounded-md border border-border">
            <QRCodeCanvas id="hub-qr-canvas" value={hubUrl} size={220} level="H" fgColor="#0A0D10" includeMargin />
          </div>
          <div className="flex flex-wrap gap-3 mt-6 justify-center">
            <Button onClick={download} data-testid="download-qr-btn" className="bg-primary text-primary-foreground"><Download className="w-4 h-4 mr-2" /> Download PNG</Button>
            <Button onClick={printCard} data-testid="print-card-btn" variant="outline"><Printer className="w-4 h-4 mr-2" /> Print table card</Button>
          </div>
        </div>

        <div className="bg-ink text-cream rounded-md p-8 flex flex-col justify-between" style={{ borderTop: `3px solid ${accent}` }}>
          <div>
            <p className="uppercase tracking-[0.3em] text-[10px] mb-4" style={{ color: accent }}>{biz.business_type}</p>
            <h2 className="font-serif text-3xl mb-2">{biz.name}</h2>
            <p className="text-cream-muted text-sm mb-8">{biz.tagline}</p>
            <div className="flex items-center gap-2 mb-2 text-cream-muted text-xs uppercase tracking-widest"><QrCode className="w-4 h-4" style={{ color: accent }} /> Your hub link</div>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-md p-3">
              <span className="text-sm text-cream truncate flex-1" data-testid="hub-url">{hubUrl}</span>
              <button onClick={copyLink} data-testid="copy-qr-link" className="text-cream-muted hover:text-cream">
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <p className="text-cream-muted text-xs mt-8">Tip: place the printed card in rooms, at reception and on dining tables so guests can scan and access your concierge instantly.</p>
        </div>
      </div>
    </div>
  );
}
